import { createFileRoute, useNavigate } from "@tanstack/react-router";
import Card from "@mui/material/Card";
import { PageContainer, PageHeader } from "@/components/layout/page";
import { Breadcrumbs } from "@/components/nav/breadcrumbs";
import { DataTable, type Column } from "@/components/data/data-table";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { EmptyState } from "@/components/feedback/states";
import { type PipelineExecution, usePipeline, usePipelineExecutions } from "@/api/pipelines";
import { useLiveInvalidate } from "@/lib/live";
import { PULSE_SX, usePulse } from "@/stores/live-runs";
import { fmtDuration, fmtNumber } from "@/lib/format";

export const Route = createFileRoute("/_app/pipelines/$pipelineId/runs/")({
  component: RunsList,
});

function RunsList() {
  const { pipelineId } = Route.useParams();
  const navigate = useNavigate();
  const pipeline = usePipeline(pipelineId);
  const runs = usePipelineExecutions(pipelineId, { size: 50 });
  useLiveInvalidate([["pipelines", "executions", pipelineId]]);
  const pulse = usePulse();

  const columns: Column<PipelineExecution>[] = [
    { id: "status", header: "Status", cell: (r) => <StatusChip status={r.status} /> },
    { id: "trigger", header: "Trigger", cell: (r) => r.triggerType ?? "—", nowrap: true },
    { id: "read", header: "Read", align: "right", cell: (r) => fmtNumber(r.recordsRead) },
    { id: "written", header: "Written", align: "right", cell: (r) => fmtNumber(r.recordsWritten) },
    { id: "failed", header: "Failed", align: "right", cell: (r) => fmtNumber(r.recordsFailed) },
    { id: "duration", header: "Duration", align: "right", cell: (r) => fmtDuration(r.durationMs) },
    {
      id: "started",
      header: "Started",
      cell: (r) => <RelativeTime value={r.startedAt} />,
      sortValue: (r) => r.startedAt,
    },
  ];

  const name = pipeline.data?.name ?? pipelineId;

  return (
    <PageContainer>
      <PageHeader
        title={`${name} — runs`}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: "Pipelines", to: "/pipelines" },
              { label: name, to: `/pipelines/${pipelineId}` },
              { label: "Runs" },
            ]}
          />
        }
      />
      <Card>
        <DataTable
          columns={columns}
          data={runs.data?.content}
          rowSx={(r) => (pulse(r.id) ? PULSE_SX : undefined)}
          isLoading={runs.isLoading}
          error={runs.error}
          onRetry={() => runs.refetch()}
          rowKey={(r) => r.id}
          onRowClick={(r) =>
            navigate({
              to: "/pipelines/$pipelineId/runs/$runId",
              params: { pipelineId, runId: r.id },
            })
          }
          empty={
            <EmptyState title="No runs yet" description="Trigger this pipeline to see runs." />
          }
        />
      </Card>
    </PageContainer>
  );
}
